import featureAnnouncementContent from './featureAnnouncementContent';

const FEATURE_ANNOUNCEMENT_REQUIRED_FIELDS = ['id', 'date', 'title'];

const stripWrappingQuotes = (value = '') => {
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith('\'') && value.endsWith('\''))) {
        return value.slice(1, -1);
    }

    return value;
};

const normalizeMarkdownAssetPath = (asset) => {
    if (typeof asset === 'string') {
        return asset;
    }

    if (asset && typeof asset.default === 'string') {
        return asset.default;
    }

    throw new Error('Unable to resolve feature announcement asset path');
};

export const createFeatureAnnouncementContext = () => featureAnnouncementContent;

export const discoverFeatureAnnouncementAssetPaths = (context = createFeatureAnnouncementContext()) => {
    const uniqueAssetPaths = new Set();

    context.keys()
        .filter(key => key.startsWith('./'))
        .sort()
        .forEach((key) => {
            uniqueAssetPaths.add(normalizeMarkdownAssetPath(context(key)));
        });

    return [...uniqueAssetPaths];
};

export const parseFeatureAnnouncementFrontmatter = (markdown) => {
    const match = markdown.match(/^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?([\s\S]*)$/);

    if (!match) {
        throw new Error('Feature announcement markdown is missing frontmatter');
    }

    const [, frontmatterBlock, content = ''] = match;
    const metadata = frontmatterBlock
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .reduce((accumulator, line) => {
            const separatorIndex = line.indexOf(':');

            if (separatorIndex === -1) {
                throw new Error(`Invalid frontmatter line: ${line}`);
            }

            const key = line.slice(0, separatorIndex).trim();
            const value = stripWrappingQuotes(line.slice(separatorIndex + 1).trim());

            accumulator[key] = value;
            return accumulator;
        }, {});

    return {
        metadata,
        content: content.trim(),
    };
};

export const parseFeatureAnnouncementMarkdown = (markdown, contentPath) => {
    const { metadata, content } = parseFeatureAnnouncementFrontmatter(markdown);

    FEATURE_ANNOUNCEMENT_REQUIRED_FIELDS.forEach((field) => {
        if (!metadata[field]) {
            throw new Error(`Feature announcement ${contentPath} is missing required field: ${field}`);
        }
    });

    if (!content) {
        throw new Error(`Feature announcement ${contentPath} must include markdown content`);
    }

    if ((metadata.ctaLabel && !metadata.ctaHref) || (!metadata.ctaLabel && metadata.ctaHref)) {
        throw new Error(`Feature announcement ${contentPath} must define both ctaLabel and ctaHref together`);
    }

    return {
        id: metadata.id,
        title: metadata.title,
        summary: metadata.summary || '',
        publishedAt: metadata.date,
        content,
        contentPath,
        ctaLabel: metadata.ctaLabel,
        ctaHref: metadata.ctaHref,
    };
};

export const buildFeatureAnnouncementRegistry = (entries = []) => {
    const seenIds = new Set();

    return [...entries]
        .sort((left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime())
        .map((entry) => {
            if (seenIds.has(entry.id)) {
                throw new Error(`Duplicate feature announcement id: ${entry.id}`);
            }

            seenIds.add(entry.id);
            return entry;
        });
};

export const formatFeatureAnnouncementDate = (publishedAt) => new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
}).format(new Date(publishedAt));

export const loadFeatureAnnouncements = async ({
    assetPaths = discoverFeatureAnnouncementAssetPaths(),
    fetchImpl,
} = {}) => {
    const resolvedFetch = fetchImpl || (typeof window !== 'undefined' ? window.fetch.bind(window) : undefined);

    if (!resolvedFetch) {
        throw new Error('Fetch is not available');
    }

    const announcements = await Promise.all(assetPaths.map(async (contentPath) => {
        const response = await resolvedFetch(contentPath);

        if (!response.ok) {
            throw new Error(`Unable to load feature announcement markdown from ${contentPath}`);
        }

        return parseFeatureAnnouncementMarkdown(await response.text(), contentPath);
    }));

    return buildFeatureAnnouncementRegistry(announcements);
};
